import styled, { css, keyframes } from 'styled-components'
import { colors, device, fonts, size } from '../../../styles/GlobalVariables'
import { CARDS_MEDIA } from './StoriesCards.style'
import { RING_THIRDS, ringMask } from './StoriesRail.style'

/**
 * 13 Shorts controls, Figma "Mobile_Shorts" (375x666) and "Desktop_Shorts"
 * Option A (514x914 player). Every number is the design's, in px.
 */
const desktop = device.regular
const CONTROL_BG = 'rgba(0, 0, 0, 0.4)'
const SEGMENT_PENDING = 'rgba(255, 255, 255, 0.4)'
/** the design's button states (Close, Share, Sound, Play): hover 60%, pressed 80% */
const CONTROL_BG_HOVER = 'rgba(0, 0, 0, 0.6)'
const CONTROL_BG_PRESSED = 'rgba(0, 0, 0, 0.8)'
/** the ad's countdown and label */
const AD_CHIP_BG = '#3a3f4d'
const safeTop = 'env(safe-area-inset-top, 0px)'
const safeBottom = 'env(safe-area-inset-bottom, 0px)'

export const OverlayRoot = styled.div<{ $hidden: boolean }>`
  position: absolute;
  inset: 0;
  pointer-events: none;
  font-family: ${fonts.narkis};
  color: #fff;
  /* a held press shows the clip alone, like Instagram */
  opacity: ${props => (props.$hidden ? 0 : 1)};
  transition: opacity 0.15s ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`

/** what turns with a group's face (D36): its own lines, caption and shade */
export const FaceChromeRoot = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  font-family: ${fonts.narkis};
  color: #fff;
`

/**
 * Player Top / Bottom Gradient (24475:48780, 24472:48790): black 35% to 0
 * over the top 100px (desktop 140), 0 to 70% over the bottom 200px (260)
 */
const scrims = (top: number, bottom: number) => css`
  background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0.35) 0%,
        rgba(0, 0, 0, 0.12) 55%,
        rgba(0, 0, 0, 0) 100%
      )
      top / 100% ${top}px no-repeat,
    linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0.3) 45%,
        rgba(0, 0, 0, 0.7) 100%
      )
      bottom / 100% ${bottom}px no-repeat;
`

export const Shade = styled.div`
  position: absolute;
  inset: 0;
  ${scrims(100, 200)}

  ${desktop} {
    ${scrims(140, 260)}
  }
`

/** one segment per clip; the first is the rightmost (M_Steps 351, D_Steps 474, 12px from the top) */
export const Segments = styled.div`
  position: absolute;
  top: calc(12px + ${safeTop});
  left: 12px;
  right: 12px;
  display: flex;
  gap: 4px;

  ${desktop} {
    top: 12px;
    left: 20px;
    right: 20px;
  }
`

/** no shadow (the design's components) */
export const Segment = styled.span`
  position: relative;
  flex: 1;
  height: 2px;
  border-radius: 1px;
  overflow: hidden;
  background-color: ${SEGMENT_PENDING};
`

/** a full bar pushed out of view by the part of the clip still to come */
const fillBar = css`
  position: absolute;
  inset: 0;
  background-color: #fff;

  @media (prefers-reduced-motion: reduce) {
    transition: none !important;
  }
`

export const SegmentFill = styled.span`
  ${fillBar}
  border-radius: 1px;
`

/* keyboard focus; a tap draws no highlight (Android's light blue is not in
   the design) */
const focusRing = css`
  -webkit-tap-highlight-color: transparent;

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
`

/** hover only where there is a pointer that hovers: a tap leaves no hover behind */
const pressStates = (hover: string, pressed: string) => css`
  @media (hover: hover) {
    &:hover:not(:disabled) {
      background-color: ${hover};
    }
  }

  &:active:not(:disabled) {
    background-color: ${pressed};
  }
`

export const RoundButton = styled.button`
  pointer-events: auto;
  position: relative;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background-color: ${CONTROL_BG};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  ${focusRing}
  ${pressStates(CONTROL_BG_HOVER, CONTROL_BG_PRESSED)}

  /* a button never goes away: unavailable, it stays dimmed (share and sound
     during an ad) */
  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`

export const CloseButton = styled(RoundButton)`
  position: absolute;
  top: calc(24px + ${safeTop});
  right: 12px;
  /* above the loading frame, the one control the design leaves undimmed */
  z-index: 1;

  ${desktop} {
    top: 24px;
    right: 20px;
  }
`

/** share over sound, bottom left */
export const Actions = styled.div<{ $sliding: boolean }>`
  position: absolute;
  left: 12px;
  bottom: calc(56px + ${safeBottom});
  display: flex;
  flex-direction: column;
  gap: 18px;

  ${desktop} {
    left: 20px;
    bottom: 72px;
    gap: 20px;
  }

  /* the cards' slide carries the clip out of the stage: the buttons would
     hang where it was, so they wait for the next episode, as its caption */
  @media ${CARDS_MEDIA} {
    visibility: ${props => (props.$sliding ? 'hidden' : 'visible')};
  }
`

/**
 * Mobile: the line's top at 626 of 666 in the design's 20px text box (18px at
 * 110%: a line box of 19.8 put Chromium's baseline a pixel high). Desktop:
 * 20px at line-height 100%, 20px from the sides, 16px above the bottom, no
 * text shadow (only the bottom scrim darkens it). The ink of the letters stays
 * inside the 20px line, but some (ץ) reach 2px past their width, so 2px of
 * side padding keeps them inside the clip the ellipsis needs.
 */
export const Caption = styled.p`
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: calc(14px + ${safeBottom});
  margin: 0;
  font-weight: 600;
  font-size: 18px;
  line-height: 20px;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${desktop} {
    left: 18px;
    right: 18px;
    bottom: 16px;
    padding: 0 2px;
    font-weight: 700;
    font-size: 20px;
    line-height: 1;
  }
`

/** mobile: the design draws it 0.5px left of the centre (158 of 375) */
export const CenterPlay = styled.button<{ $hoverOnly?: boolean }>`
  pointer-events: auto;
  position: absolute;
  top: 50%;
  left: 50%;
  width: 58px;
  height: 58px;
  margin: -29px 0 0 -29.5px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background-color: ${CONTROL_BG};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  ${focusRing}
  ${pressStates(CONTROL_BG_HOVER, CONTROL_BG_PRESSED)}

  /* the pause: a desktop mouse over the player, or the keyboard on it */
  ${props =>
    props.$hoverOnly &&
    css`
      display: none;

      @media (hover: hover) and (min-width: ${size.regular}px) {
        .vv-stage:hover &,
        &:focus-visible {
          display: flex;
        }
      }
    `}

  ${desktop} {
    width: 66px;
    height: 66px;
    margin: -33px 0 0 -33px;
  }
`

/** full bleed, filled from the left as drawn */
export const Progress = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 2px;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.4);

  ${desktop} {
    height: 4px;
  }
`

export const ProgressFill = styled.div`
  ${fillBar}
  border-radius: 0 4px 4px 0;
`

/**
 * The strip a finger or a mouse can hit around the progress line: it draws
 * nothing, the line keeps the design's 2 / 4px. It ends 20px above the
 * bottom, under the caption's box (the caption sits 20px up).
 */
export const SeekArea = styled.div`
  pointer-events: auto;
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 20px;
  touch-action: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:focus-visible {
    outline: 2px solid #fff;
    outline-offset: -2px;
  }
`

/** desktop only: the pairs 20px off each side of the player, centered on it */
export const Side = styled.div<{ $side: 'left' | 'right'; $disabled: boolean }>`
  display: none;

  ${desktop} {
    pointer-events: auto;
    position: absolute;
    top: 50%;
    display: flex;
    align-items: center;
    gap: 20px;
    direction: ltr;
    transform: translateY(-50%);
    opacity: ${props => (props.$disabled ? 0.4 : 1)};
    /* the whole pair is dimmed once (the ad frame), not each button again */
    ${props =>
      props.$disabled &&
      css`
        button:disabled {
          opacity: 1;
        }
      `}
    ${props =>
      props.$side === 'left'
        ? css`
            right: calc(100% + 20px);
          `
        : css`
            left: calc(100% + 20px);
          `}
  }
`

export const ChevronButton = styled(RoundButton)`
  background-color: #fff;
  ${pressStates('#e0e0e0', '#c2c2c2')}

  &:disabled {
    cursor: default;
  }
`

/**
 * 44px ring (1.917px) in the brand thirds, as the rail's bubbles. The left
 * button is the right one turned over, colours and icon together, as drawn.
 * Beside the cards they stand in for them only where the cards are not drawn.
 */
export const RingButton = styled.button<{
  $flip?: boolean
  $besideCards?: boolean
}>`
  pointer-events: auto;
  position: relative;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transform: ${props => (props.$flip ? 'rotate(180deg)' : 'none')};
  ${focusRing}
  ${pressStates('rgba(255, 255, 255, 0.15)', 'rgba(255, 255, 255, 0.3)')}

  &:focus-visible {
    outline-offset: 3.92px;
  }

  ${props =>
    props.$besideCards &&
    css`
      @media ${CARDS_MEDIA} {
        display: none;
      }
    `}

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: ${RING_THIRDS};
    ${ringMask('1.917px')}
  }

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`

/**
 * Frame 1931874429/30 (24639:411758, 24639:411759): the countdown and the label
 * 10px after it, one row 34 high. The row sits on the sound button's edge:
 * 12px on the phone, 20px on the desktop stage. Figma centres the label (23) at
 * 5.5px; a half pixel makes the browser round its text one way, so it takes 6px
 * (the Hebrew ink sits a quarter pixel high in the line, this is the closer one).
 */
export const AdChips = styled.div`
  position: absolute;
  top: calc(24px + ${safeTop});
  left: 12px;
  direction: ltr;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  pointer-events: none;

  ${desktop} {
    top: 24px;
    left: 20px;
  }
`

/** Done Tick 1: the arrow comes in as the ring goes */
const arrowIn = keyframes`
  from {
    opacity: 0;
  }
`

/** Done Tick 2-5: twice 3px towards the next clip and back, 0.18s each way */
const arrowNudge = keyframes`
  50% {
    transform: translateX(-3px);
  }
`

/**
 * Ad Countdown (24439:48720): 34px on #3A3F4D, a 2px band, the track white
 * 25%, the pink arc the time left from 12 o'clock clockwise. The design moves
 * it in 4 linear ticks a second, the ad's own time reports. Seconds=Done: the
 * same disc with an arrow, 0.24s linear from the last tick; the arrow rests
 * 0.35s, then nudges.
 */
export const AdCountdown = styled.button<{ $done: boolean }>`
  position: relative;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background-color: ${AD_CHIP_BG};
  /* the number is SVG text: it takes the overlay's font */
  font-family: inherit;
  ${focusRing}

  &:enabled {
    pointer-events: auto;
    cursor: pointer;
  }

  svg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
  }

  g,
  text {
    opacity: ${props => (props.$done ? 0 : 1)};
    transition: opacity 0.24s linear;
  }

  circle {
    fill: none;
    stroke-width: 2px;
  }

  circle:first-child {
    stroke: rgba(255, 255, 255, 0.25);
  }

  circle + circle {
    stroke: ${colors.pink};
    /* the ad reports its time 4 times a second, as the design ticks */
    transition: stroke-dashoffset 0.25s linear;
  }

  text {
    fill: #fff;
    font-size: 16px;
    font-weight: 400;
  }

  path {
    fill: none;
    stroke: #fff;
    stroke-width: 1.5px;
    stroke-linecap: round;
    stroke-linejoin: round;
    animation: ${arrowIn} 0.24s linear, ${arrowNudge} 0.36s ease-in-out 0.59s 2;
  }

  @media (prefers-reduced-motion: reduce) {
    g,
    text,
    circle + circle {
      transition: none;
    }

    path {
      animation: none;
    }
  }
`

/**
 * Ad Label (24477:50064): Narkiss Regular 14, its "normal" line 17px, 62x23.
 * The word sits in the middle of the chip, 8px of padding each side in Figma
 */
export const AdLabel = styled.span`
  margin-top: 6px;
  box-sizing: border-box;
  min-width: 62px;
  text-align: center;
  padding: 3.5px 8px 2.5px;
  border-radius: 4px;
  background-color: ${AD_CHIP_BG};
  font-size: 14px;
  font-weight: 400;
  line-height: 17px;
  color: #fff;
  white-space: nowrap;
`

/**
 * Not in the design. It sits 20px under the close button, where nothing else
 * is drawn: at the bottom it covered the share button on narrow players.
 */
export const Toast = styled.div<{ $withLink?: boolean }>`
  position: absolute;
  pointer-events: ${p => (p.$withLink ? 'auto' : 'none')};
  left: 50%;
  top: calc(78px + ${safeTop});
  transform: translateX(-50%);
  padding: 8px 16px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0.7);
  font-weight: 600;
  font-size: 16px;
  line-height: 1.2;
  /* one line; on a narrow player (a phone on its side) it wraps inside it */
  width: max-content;
  max-width: calc(100% - 24px);
  box-sizing: border-box;
  text-align: center;

  ${desktop} {
    top: 78px;
  }
`

export const SrOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`

/** the link to copy by hand, selected when shown */
export const ToastLink = styled.input`
  display: block;
  width: 100%;
  min-width: 240px;
  margin-top: 8px;
  padding: 6px 8px;
  border: 0;
  border-radius: 4px;
  box-sizing: border-box;
  background-color: #fff;
  color: #000;
  font: inherit;
  font-weight: 400;
  font-size: 14px;
  direction: ltr;
  text-align: left;
`
